/**
 * ReportingQueueCard — overdue + due monthly digests across the portfolio,
 * with a one-click "Send now" button that fires the existing digest endpoint.
 */
import React, { useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Mail, AlertCircle, Clock, CheckCircle2, Loader2, Send } from "lucide-react";

function Row({ item, tone, onSent }) {
  const [sending, setSending] = useState(false);
  const send = async (e) => {
    e.stopPropagation();
    setSending(true);
    try {
      await api.post(`/clients/${item.client_id}/send-digest`);
      toast.success(`Digest sent to ${item.name}.`);
      onSent?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send digest");
    } finally {
      setSending(false);
    }
  };
  return (
    <div
      data-testid={`reporting-row-${item.client_id}`}
      className="flex items-center gap-2 px-3 py-2 hover:bg-[#f7fafc] transition-colors"
    >
      <div className={`h-5 w-5 rounded-full ${tone.bg} flex items-center justify-center flex-none`}>
        <tone.Icon className={`h-3 w-3 ${tone.fg}`} strokeWidth={2.4} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold text-[#1a202c] truncate">{item.name}</div>
        <div className="text-[10px] text-[#a0aec0] truncate font-mono-tight">{item.report_email}</div>
      </div>
      {tone.action && (
        <button
          onClick={send}
          disabled={sending}
          data-testid={`reporting-send-${item.client_id}`}
          className="text-[10px] font-semibold rounded-full bg-[#4fd1c5]/10 hover:bg-[#4fd1c5] hover:text-white text-[#2c7a7b] px-2.5 py-1 inline-flex items-center gap-1 transition-colors flex-none"
        >
          {sending ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Send className="h-2.5 w-2.5" />}
          Send
        </button>
      )}
    </div>
  );
}

export default function ReportingQueueCard({ data, loading, onReload }) {
  if (loading) {
    return <div className="card-soft p-4 animate-pulse h-[180px]" data-testid="reporting-queue-loading" />;
  }
  const overdue = data?.overdue || [];
  const due = data?.due_this_month || [];
  const sentCount = data?.counts?.sent || 0;
  const total = overdue.length + due.length;

  return (
    <div className="card-soft overflow-hidden" data-testid="reporting-queue-card">
      <div className="px-4 py-3 border-b border-[#edf2f7] flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="h-6 w-6 rounded-md bg-[#ebf8ff] border border-[#bee3f8] flex items-center justify-center">
            <Mail className="h-3 w-3 text-[#2b6cb0]" strokeWidth={2.4} />
          </div>
          <div className="text-[10px] uppercase tracking-widest text-[#4a5568] font-bold">
            Reporting queue
          </div>
        </div>
        <div className="text-[10px] text-[#a0aec0] font-mono-tight">
          {total === 0 ? (
            <span className="text-[#2c7a7b] font-semibold">All sent this month</span>
          ) : (
            <>{total} pending · <span className="text-[#2c7a7b]">{sentCount} already sent</span></>
          )}
        </div>
      </div>

      {total === 0 ? (
        <div className="px-4 py-6 text-[11px] text-[#718096] italic text-center flex items-center gap-2 justify-center">
          <CheckCircle2 className="h-4 w-4 text-[#2c7a7b]" /> Every client with reporting enabled has had their digest this month.
        </div>
      ) : (
        <div className="divide-y divide-[#edf2f7] max-h-[280px] overflow-y-auto">
          {overdue.map((it) => (
            <Row
              key={it.client_id}
              item={it}
              tone={{ bg: "bg-[#fed7d7]", fg: "text-[#c53030]", Icon: AlertCircle, action: true }}
              onSent={onReload}
            />
          ))}
          {due.map((it) => (
            <Row
              key={it.client_id}
              item={it}
              tone={{ bg: "bg-[#fefcbf]", fg: "text-[#975a16]", Icon: Clock, action: true }}
              onSent={onReload}
            />
          ))}
        </div>
      )}
    </div>
  );
}
